/**
 * File Upload API Client
 *
 * Handles all API calls related to file uploads for denominational documents.
 */

import apiClient from './client';

export interface UploadedFile {
  id: string;
  original_filename: string;
  file_type: 'pdf' | 'docx' | 'md';
  file_size_bytes: number;
  mime_type: string;
  storage_key: string;
  processing_status: 'pending' | 'processing' | 'completed' | 'failed';
  approval_status: 'pending_review' | 'approved' | 'rejected';
  denomination_id: string;
  document_type: string;
  is_official: boolean;
  trigger_words?: string;
  folder_path?: string;
  created_at: string;
  uploaded_by?: string;
  extracted_title?: string;
  extracted_author?: string;
  extracted_page_count?: number;
  extracted_word_count?: number;
  chunks_created?: number;
  chunking_strategy?: string;
  processing_error?: string;
}

export interface FileListResponse {
  files: UploadedFile[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface FileUploadParams {
  file: File;
  denomination_id: string;
  document_type: string;
  is_official: boolean;
  folder_path?: string;
}

export interface ProcessFileParams {
  chunking_strategy: 'paragraphs' | 'sections' | 'pages' | 'articles';
}

export interface DownloadUrlResponse {
  download_url: string;
  expires_in: number;
  filename: string;
}

/**
 * Upload a file (PDF, DOCX, or Markdown)
 */
export const uploadFile = async (params: FileUploadParams): Promise<UploadedFile> => {
  const formData = new FormData();
  formData.append('file', params.file);
  formData.append('denomination_id', params.denomination_id);
  formData.append('document_type', params.document_type);
  formData.append('is_official', params.is_official.toString());
  if (params.folder_path) {
    formData.append('folder_path', params.folder_path);
  }

  const response = await apiClient.post<UploadedFile>(
    '/api/file-uploads/upload',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000, // 60 seconds for file upload
    }
  );

  return response.data;
};

/**
 * List uploaded files with pagination and filtering
 */
export const listFiles = async (
  limit = 20,
  offset = 0,
  filters?: {
    denomination_id?: string;
    file_type?: string;
    processing_status?: string;
    is_official?: boolean;
  }
): Promise<FileListResponse> => {
  const params: any = { limit, offset };

  if (filters) {
    if (filters.denomination_id) params.denomination_id = filters.denomination_id;
    if (filters.file_type) params.file_type = filters.file_type;
    if (filters.processing_status) params.processing_status = filters.processing_status;
    if (filters.is_official !== undefined) params.is_official = filters.is_official;
  }

  const response = await apiClient.get<FileListResponse>('/api/file-uploads', { params });
  return response.data;
};

/**
 * Get details of a specific file
 */
export const getFile = async (fileId: string): Promise<UploadedFile> => {
  const response = await apiClient.get<UploadedFile>(`/api/file-uploads/${fileId}`);
  return response.data;
};

/**
 * Process an uploaded file (parse and chunk)
 */
export const processFile = async (
  fileId: string,
  params: ProcessFileParams
): Promise<UploadedFile> => {
  const response = await apiClient.post<UploadedFile>(
    `/api/file-uploads/${fileId}/process`,
    params
  );
  return response.data;
};

/**
 * Get processing status of a file
 */
export const getFileStatus = async (fileId: string): Promise<{
  id: string;
  processing_status: string;
  processing_started_at?: string;
  processing_completed_at?: string;
  processing_error?: string;
  chunks_created?: number;
}> => {
  const response = await apiClient.get(`/api/file-uploads/${fileId}/status`);
  return response.data;
};

/**
 * Get download URL for a file
 */
export const getDownloadUrl = async (fileId: string): Promise<DownloadUrlResponse> => {
  const response = await apiClient.get<DownloadUrlResponse>(
    `/api/file-uploads/${fileId}/download`
  );
  return response.data;
};

/**
 * Delete a file (soft delete)
 */
export const deleteFile = async (fileId: string): Promise<void> => {
  await apiClient.delete(`/api/file-uploads/${fileId}`);
};

/**
 * Get processing logs for a file
 */
export const getProcessingLogs = async (fileId: string): Promise<{
  logs: Array<{
    id: string;
    log_level: string;
    log_message: string;
    created_at: string;
  }>;
  total: number;
}> => {
  const response = await apiClient.get(`/api/file-uploads/${fileId}/logs`);
  return response.data;
};

/**
 * Update file approval status (approve/reject for knowledge base)
 */
export const updateApprovalStatus = async (
  fileId: string,
  approvalStatus: 'approved' | 'rejected'
): Promise<{
  id: string;
  approval_status: string;
  updated_at: string;
}> => {
  const response = await apiClient.patch(
    `/api/file-uploads/${fileId}/approval-status`,
    { approval_status: approvalStatus }
  );
  return response.data;
};

export interface UpdateFileMetadataParams {
  denomination_id?: string;
  document_type?: string;
  is_official?: boolean;
  trigger_words?: string;
  extracted_title?: string;
  folder_path?: string;
}

/**
 * Update file metadata (denominational context and trigger words)
 */
export const updateFileMetadata = async (
  fileId: string,
  metadata: UpdateFileMetadataParams
): Promise<{
  id: string;
  denomination_id: string;
  document_type: string;
  is_official: boolean;
  trigger_words?: string;
  extracted_title?: string;
  folder_path?: string;
  updated_at: string;
}> => {
  const response = await apiClient.patch(
    `/api/file-uploads/${fileId}/metadata`,
    metadata
  );
  return response.data;
};
